import type React from 'react';
import { useEffect, useState } from 'react';
import EmailLink from '~/components2/EmailLink';
import Icon from '~/components2/Icon';
import type { AssetLayer } from './../store';

interface StandardTableProps {
  title: string;
  description: string;
  rows: AssetLayer[];
  button?: React.ReactNode;
  notes?: string;
  'data-testid'?: string;
}

const tdClasses = 'p-1';

const StandardTable = ({ title, description, rows, button, notes, 'data-testid': testId }: StandardTableProps) => {
  const [isTableOpen, setIsTableOpen] = useState(true);
  const [hasPipline, setHasPipeline] = useState(false);

  useEffect(() => {
    const p = rows.find((row) => row.type === 'Pipeline');

    if (p) {
      setHasPipeline(true);
    } else {
      setHasPipeline(false);
    }
  }, [rows]);

  return (
    <div className="w-full" data-testid={testId}>
      <div
        onClick={() => setIsTableOpen((prev) => !prev)}
        className="flex cursor-pointer items-center gap-3 hover:bg-grey-20"
      >
        <Icon icon={isTableOpen ? 'chevronUp' : 'chevronDown'} />
        <div className="text-lg">{title}</div>
      </div>

      {isTableOpen && (
        <>
          <div className="mb-2 flex items-center justify-between">
            <div className="text-xs">{description}</div>
            {button}
          </div>
          <table className="w-full text-xs">
            <thead className="border-b border-b-grey-20 font-bold">
              <tr>
                <td className={tdClasses}>Name</td>
                <td className={tdClasses}>Type</td>
                <td className={tdClasses}>Source Date</td>
                <td className={tdClasses}>Refresh Frequency</td>
                <td className={tdClasses}>Latest Refresh</td>
              </tr>
            </thead>

            <tbody>
              {rows.map((row, i) => (
                <tr key={row.id} className="border-b border-b-grey-20">
                  <td className={tdClasses}>{row.name || ''}</td>
                  <td className={tdClasses}>{row.type || ''}</td>
                  <td className={tdClasses}>{row.sourceDate}</td>
                  <td className={tdClasses}>{row.refreshCadence}</td>
                  <td className={tdClasses}>{row.refreshDate || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {notes && (
            <div className="pt-1 text-2xs italic">
              <strong className="font-bold">Note: </strong>
              {notes}
            </div>
          )}
          {hasPipline && (
            <div className="text-2xs italic">
              <strong className="font-bold">Note: </strong>
              Pipeline data is not available for export to CAD or GIS. Please <EmailLink>contact us</EmailLink> for more
              information.
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default StandardTable;
